/*
 * Form helpers shared by the create/edit forms: pure functions, no React and nothing that touches `window`.
 */
import { slugify } from '@/lib/format';

/** A form whose slug follows the name until the slug is typed by hand. */
export interface SlugValues {
  name: string;
  slug: string;
  slugTouched: boolean;
}

/** The name feeds the slug until the slug is typed by hand. */
export const applyName = <V extends SlugValues>(v: V, name: string): V => ({ ...v, name, slug: v.slugTouched ? v.slug : slugify(name) });

/** A hand-typed slug: lowercase, and it stops following the name. */
export const applySlug = <V extends SlugValues>(v: V, raw: string): V => ({ ...v, slugTouched: true, slug: raw.toLowerCase() });

/**
 * The message under a field: the API's wins; the local one shows once the form was submitted, or while the user types
 * when the field validates `live`.
 */
export function shownError<K extends string>(field: K, local: Record<K, string | undefined>, serverFields: Record<string, string | undefined>, submitted: boolean, live = false): string | undefined {
  return serverFields[field] || (submitted || live ? local[field] : undefined);
}

/** The first field (in declaration order) whose local validation failed. */
export function firstInvalidField<K extends string>(local: Record<K, string | undefined>): K | undefined {
  return (Object.keys(local) as K[]).find((k) => local[k]);
}

/** A PATCH body with nothing in it: the person changed nothing, so there is nothing to save. */
export const isEmptyBody = (body: object) => Object.keys(body).length === 0;
